import { useContext, useState, type MouseEvent } from "react";
import { ColorModeContext, tokens } from "../../theme";
import {
  useTheme,
  Badge,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  Typography,
} from "@mui/material";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import { useProSidebar } from "react-pro-sidebar";
import InitialsAvatar from "../../components/InitialsAvatar";
import BusinessSwitcher from "../../components/BusinessSwitcher";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { getFrontDeskCounts } from "../../api/bookings";
import { useBookingScope } from "../../hooks/useBookingScope";
import { useFrontDeskCounts } from "../../hooks/useFrontDeskCounts";

const SSO_ORIGIN = process.env.REACT_APP_SSO_ORIGIN ?? "https://sso.chatbot.mn";

/** 44 px touch targets (C4 / admin looks). */
const iconButton = { width: 44, height: 44 };

const Topbar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const colorMode = useContext(ColorModeContext);
  const { toggleSidebar, broken } = useProSidebar();
  const { userInfo, logout } = useAuth();
  const u = userInfo();
  const navigate = useNavigate();
  // Bell: late payments + refunds owed for the selected business.
  const { effectiveBusinessId } = useBookingScope();
  const { total: attention } = useFrontDeskCounts(effectiveBusinessId, getFrontDeskCounts);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const openMenu = (e: MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  const handleLogout = () => {
    closeMenu();
    logout({ useOIDCLogout: true });
  };

  const handleSsoProfile = () => {
    closeMenu();
    window.open(`${SSO_ORIGIN}/account`, "_blank", "noopener");
  };

  // Match the Figma chrome treatment: dense padding, mode-aware bg, subtle
  // bottom hairline that separates the topbar from the page surface (page
  // bg is one shade lighter than chrome in dark mode so the hairline is
  // what makes the topbar read as a band).
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        px: { xs: 1, sm: 3 },
        py: { xs: 0.5, sm: 1 },
        gap: 1,
        backgroundColor: colors.primary[theme.palette.mode === "dark" ? 500 : 400],
        borderBottom: `1px solid ${
          theme.palette.mode === "dark" ? colors.primary[600] : colors.grey[800]
        }`,
        position: "sticky",
        top: 0,
        zIndex: 1000,
      }}
    >
      <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
        {/* Phone / narrow window: the sidebar is a drawer behind this button. */}
        {broken ? (
          <IconButton onClick={() => toggleSidebar()} aria-label="Цэс" sx={iconButton}>
            <MenuOutlinedIcon />
          </IconButton>
        ) : null}
        {/* Global business scope — single source of truth for every page. */}
        <BusinessSwitcher />
      </Box>
      <Box display="flex" alignItems="center" gap={0.5}>
        <IconButton onClick={colorMode.toggleColorMode} aria-label="Цайвар / харанхуй" sx={iconButton}>
          {theme.palette.mode === "dark" ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
        </IconButton>
        <IconButton onClick={() => navigate("/front-desk")} aria-label="Анхаарах зүйлс" sx={iconButton}>
          <Badge badgeContent={attention} color="error" invisible={attention === 0} max={99}>
            <NotificationsOutlinedIcon />
          </Badge>
        </IconButton>
        <IconButton onClick={() => navigate("/business/update")} aria-label="Бизнесийн тохиргоо" sx={iconButton}>
          <SettingsOutlinedIcon />
        </IconButton>
        <IconButton onClick={openMenu} aria-label="Хэрэглэгчийн цэс" sx={{ ...iconButton, ml: 0.5 }}>
          <InitialsAvatar name={u.name} email={u.email} size={32} />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={closeMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{ sx: { minWidth: 240, mt: 1 } }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {u.name || "Хэрэглэгч"}
            </Typography>
            {u.email ? (
              <Typography variant="caption" color={colors.grey[300]}>
                {u.email}
              </Typography>
            ) : null}
          </Box>
          <Divider />
          <MenuItem onClick={handleSsoProfile}>
            <ListItemIcon>
              <OpenInNewOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Профайл
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Гарах
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

export default Topbar;
